import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { User, Package, Clock, MapPin, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, API } from "@/App";
import axios from "axios";

const ProfilePage = () => {
  const { user, token, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (e) {
      console.error("Error fetching orders:", e);
    }
    setLoading(false);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: "En attente", color: "bg-yellow-100 text-yellow-800" },
      confirmed: { label: "Confirmée", color: "bg-blue-100 text-blue-800" },
      processing: { label: "En préparation", color: "bg-purple-100 text-purple-800" },
      shipped: { label: "Expédiée", color: "bg-indigo-100 text-indigo-800" },
      delivered: { label: "Livrée", color: "bg-green-100 text-green-800" },
      cancelled: { label: "Annulée", color: "bg-red-100 text-red-800" }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="profile-page">
      <div className="container-custom py-12">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#1D3557]" style={{ fontFamily: 'Fraunces, serif' }}>
              Mon Compte
            </h1>
            <p className="text-stone-600 mt-2">
              Bienvenue, {user?.first_name} {user?.last_name}
            </p>
          </div>
          <Button onClick={logout} variant="outline" className="text-[#E63946] border-[#E63946] hover:bg-[#E63946] hover:text-white rounded-full">
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>

        <Tabs defaultValue="orders" className="space-y-8">
          <TabsList className="bg-white shadow-sm rounded-full p-1">
            <TabsTrigger value="orders" className="rounded-full px-6 data-[state=active]:bg-[#C65D47] data-[state=active]:text-white">
              Mes Commandes
            </TabsTrigger>
            <TabsTrigger value="profile" className="rounded-full px-6 data-[state=active]:bg-[#C65D47] data-[state=active]:text-white">
              Mon Profil
            </TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="orders" data-testid="orders-tab">
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="skeleton h-32 rounded-2xl"></div>
                ))}
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl shadow-sm">
                <div className="w-20 h-20 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-6">
                  <Package className="w-10 h-10 text-stone-300" />
                </div>
                <h3 className="text-xl font-semibold text-[#1D3557] mb-2">Aucune commande</h3>
                <p className="text-stone-500 mb-6">Vous n'avez pas encore passé de commande</p>
                <Button asChild className="bg-[#C65D47] hover:bg-[#A84A38] rounded-full">
                  <Link to="/boutique">Découvrir la boutique</Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {orders.map((order) => (
                  <div key={order.id} className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid={`order-${order.id}`}>
                    <div className="p-6 border-b border-stone-100">
                      <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-[#1D3557]/10 flex items-center justify-center">
                            <Package className="w-6 h-6 text-[#1D3557]" />
                          </div>
                          <div>
                            <p className="font-semibold text-[#1D3557]">
                              Commande #{order.id.slice(0, 8)}
                            </p>
                            <div className="flex items-center gap-2 text-sm text-stone-500">
                              <Clock className="w-4 h-4" />
                              {formatDate(order.created_at)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {getStatusBadge(order.status)}
                          <p className="text-xl font-bold text-[#C65D47]">
                            {formatPrice(order.total)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="flex items-start gap-3 mb-4 text-sm text-stone-600">
                        <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <p>
                          {order.shipping_address}, {order.shipping_postal} {order.shipping_city}, {order.shipping_country}
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-4">
                        {order.items.slice(0, 3).map((item) => (
                          <div key={item.id} className="flex items-center gap-3 bg-stone-50 rounded-lg p-2 pr-4">
                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-white">
                              <img src={item.product_image} alt={item.product_name} className="w-full h-full object-cover" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-[#1D3557] line-clamp-1">{item.product_name}</p>
                              <p className="text-xs text-stone-500">x{item.quantity}</p>
                            </div>
                          </div>
                        ))}
                        {order.items.length > 3 && (
                          <div className="flex items-center justify-center px-4 bg-stone-50 rounded-lg">
                            <p className="text-sm text-stone-500">+{order.items.length - 3} autres</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile" data-testid="profile-tab">
            <div className="bg-white rounded-2xl shadow-sm p-8">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#C65D47] to-[#1D3557] flex items-center justify-center">
                  <span className="text-white text-2xl font-bold">
                    {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                  </span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-[#1D3557]">
                    {user?.first_name} {user?.last_name}
                  </h2>
                  <p className="text-stone-500">{user?.email}</p>
                  {user?.is_admin && (
                    <Badge className="mt-2 bg-[#8C9D78]">Administrateur</Badge>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-4 bg-stone-50 rounded-xl">
                  <p className="text-sm text-stone-500 mb-1">Email</p>
                  <p className="font-medium text-[#1D3557]">{user?.email}</p>
                </div>
                <div className="p-4 bg-stone-50 rounded-xl">
                  <p className="text-sm text-stone-500 mb-1">Téléphone</p>
                  <p className="font-medium text-[#1D3557]">{user?.phone || "Non renseigné"}</p>
                </div>
                <div className="p-4 bg-stone-50 rounded-xl">
                  <p className="text-sm text-stone-500 mb-1">Entreprise</p>
                  <p className="font-medium text-[#1D3557]">{user?.company || "Non renseigné"}</p>
                </div>
                <div className="p-4 bg-stone-50 rounded-xl">
                  <p className="text-sm text-stone-500 mb-1">Membre depuis</p>
                  <p className="font-medium text-[#1D3557]">
                    {user?.created_at ? formatDate(user.created_at) : "N/A"}
                  </p>
                </div>
              </div>

              {user?.is_admin && (
                <div className="mt-8 pt-8 border-t border-stone-200">
                  <Button asChild className="bg-[#1D3557] hover:bg-[#2A4A6D] rounded-full">
                    <Link to="/admin">Accéder à l'administration</Link>
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ProfilePage;
